import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Copy, Plus, X, Loader2, Inbox, CheckCircle2, AlertTriangle } from "lucide-react";

export const VaultInbox = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/vault/inbox").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Address copied"); };
  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/vault/inbox/linked-emails", { email: email.trim() }); setEmail(""); toast.success("Address linked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link address"); } finally { setBusy(false); }
  };
  const remove = async (e) => { try { await api.delete(`/vault/inbox/linked-emails/${encodeURIComponent(e)}`); load(); } catch { toast.error("Could not remove"); } };

  if (!data) return null;
  const address = data.private_address || data.shared_address;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="vault-inbox">
      <div className="flex items-center gap-2 text-[#114A31]"><Mail className="h-5 w-5" /><span className="font-heading font-semibold">Email receipts in</span></div>
      <p className="mt-1 text-sm text-[#595D5A]">Forward any email receipt (Uber, Amazon, Stripe, hotel folios…) and it lands in your vault, read by AI, ready for your review.</p>

      {address ? (
        <div className="mt-4 flex items-center gap-2 bg-[#F4F5F3] border border-[#E5E5E5] rounded-xl px-3 py-2.5">
          <code className="flex-1 text-sm text-[#114A31] font-semibold truncate" data-testid="vault-inbox-address">{address}</code>
          <button onClick={() => copy(address)} className="p-1.5 text-[#595D5A] hover:text-[#114A31]" data-testid="vault-inbox-copy" title="Copy"><Copy className="h-4 w-4" /></button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-[#92600A] bg-[#FEF3C7] rounded-xl px-3 py-2" data-testid="vault-inbox-disabled">Email-in isn't switched on yet — check back soon.</p>
      )}

      <div className="mt-4">
        <p className="text-xs font-semibold text-[#1C1C1C]">We match receipts to you by the address you send from</p>
        <ul className="mt-2 space-y-1.5" data-testid="vault-linked-emails">
          <li className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2"><CheckCircle2 className="h-4 w-4 text-[#166534]" /><span className="flex-1 truncate">{data.primary_email}</span><span className="text-[10px] text-[#595D5A]">login</span></li>
          {data.linked_emails.map((e) => (
            <li key={e} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2" data-testid={`linked-email-${e}`}>
              <CheckCircle2 className="h-4 w-4 text-[#166534]" /><span className="flex-1 truncate">{e}</span>
              <button onClick={() => remove(e)} className="text-[#595D5A] hover:text-[#991B1B]" data-testid={`linked-email-remove-${e}`}><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add another address you forward from" data-testid="linked-email-input" className="rounded-xl" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button onClick={add} disabled={busy || !email} variant="outline" data-testid="linked-email-add-btn" className="rounded-xl border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
        </div>
      </div>

      {data.recent.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-[#1C1C1C] flex items-center gap-1"><Inbox className="h-3.5 w-3.5" /> Recently received</p>
          <ul className="mt-2 space-y-1.5" data-testid="vault-inbox-recent">
            {data.recent.slice(0, 5).map((m) => (
              <li key={m.email_id} className="flex items-center gap-2 text-xs bg-[#F9F9F8] rounded-xl px-3 py-2">
                {m.status === "processed" ? <CheckCircle2 className="h-3.5 w-3.5 text-[#166534] shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-[#92600A] shrink-0" />}
                <span className="flex-1 truncate text-[#1C1C1C]">{m.subject || "(no subject)"}</span>
                <span className="text-[#595D5A] shrink-0">{m.status === "processed" ? `${(m.receipt_ids || []).length} receipt${(m.receipt_ids || []).length === 1 ? "" : "s"}` : m.status.replace("_", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VaultInbox;
